import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { FileText, Clock, CircleCheck as CheckCircle2, Circle as XCircle, CalendarX } from 'lucide-react';
import { proposalService, parseCoverData, type Proposal, type ProposalStatus } from '@/lib/proposalService';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { format, formatDistanceToNow } from 'date-fns';

const statusConfig: Record<ProposalStatus, { label: string; icon: any; className: string }> = {
  draft: { label: 'Draft', icon: FileText, className: 'bg-gray-100 text-gray-700' },
  pending: { label: 'Awaiting Review', icon: Clock, className: 'bg-amber-50 text-amber-700 border border-amber-200' },
  approved: { label: 'Approved', icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  declined: { label: 'Declined', icon: XCircle, className: 'bg-rose-50 text-rose-700 border border-rose-200' },
  expired: { label: 'Expired', icon: CalendarX, className: 'bg-slate-100 text-slate-600 border border-slate-200' },
};

export default function PortalProposals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    proposalService.listClientProposals(user.id)
      .then(setProposals)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user?.id]);

  return (
    <div className="min-h-screen">
      <Helmet><title>Proposals | Client Portal</title></Helmet>

      <div className="w-full p-6 md:p-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 text-foreground">Proposals</h1>
          <p className="text-muted-foreground">Review and approve proposals from your agency</p>
        </div>

        {loading ? (
          <div className="py-20 text-center text-muted-foreground">Loading...</div>
        ) : proposals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-card border border-border rounded-xl">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 bg-workspace-accent/10">
              <FileText className="w-10 h-10 text-workspace-accent" />
            </div>
            <h2 className="text-xl font-bold mb-2 text-foreground">No proposals yet</h2>
            <p className="text-center max-w-md text-muted-foreground">
              When your agency sends a proposal, it will appear here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {proposals.map(p => {
              const cfg = statusConfig[p.status];
              const Icon = cfg.icon;
              const coverData = parseCoverData(p.cover_image_url);
              
              return (
                <button
                  key={p.id}
                  onClick={() => navigate(`/proposals/${p.slug}`)}
                  className="text-left rounded-2xl border border-border bg-card overflow-hidden hover:shadow-lg hover:border-workspace-accent/30 transition-all group"
                >
                  <div 
                    className="aspect-video relative overflow-hidden"
                    style={{ background: coverData.bgType === 'gradient' && coverData.gradient ? coverData.gradient : coverData.color }}
                  >
                    {coverData.image && (
                      <div
                        className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-500"
                        style={{ backgroundImage: `url(${coverData.image})`, opacity: 0.18 }}
                      />
                    )}
                    {!coverData.image && !p.cover_image_url && (
                      <div className="w-full h-full flex items-center justify-center relative z-10">
                        <FileText className="w-10 h-10 text-slate-900/10" />
                      </div>
                    )}
                    <span className={cn('absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium z-20', cfg.className)}>
                      <Icon className="w-3 h-3" /> {cfg.label}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-lg mb-1 line-clamp-1">{p.title}</h3>
                    {p.company_name && <p className="text-xs text-muted-foreground mb-3">Prepared for {p.company_name}</p>}
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{p.sent_at ? `Sent ${formatDistanceToNow(new Date(p.sent_at), { addSuffix: true })}` : '—'}</span>
                      {p.valid_until && p.status === 'pending' && (
                        <span className="font-medium text-amber-700">
                          Expires {format(new Date(p.valid_until), 'MMM d')}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
